import { connect, FieldArray, FormikValues } from 'formik'
import { useEffect } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import { v4 } from 'uuid'
import { array, object, string } from 'yup'

import Label from '@/components/Label'
import VariableInput from '@/pages/SpaceAppBuilder/components/VariableInput/VariableInput'
import Monaco from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/components/Monaco'
import { useWorkflowFeature } from '@/pages/SpaceAppBuilder/FeaturesPage/WorkflowFeatureContext'
import { ResponseStepData, StepData, Triggers, TriggerTypes } from '@/pages/SpaceAppBuilder/utilities/interfaces'

import messages from './messages'

type ResponseProps = {
  readOnly?: boolean
  trigger: Triggers
}

const getValidationSchema = (intl: IntlShape, formObject: FormikValues) => {
  if (formObject.parameters) {
    return {
      parameters: array()
        .of(
          object()
            .shape({
              value: string().required(intl.formatMessage(messages.responseValueRequired))
            })
            .required()
        )
        .required('Response is required')
        .min(1, 'Response is required')
    }
  } else {
    return {
      inputVariable: string().required('Response is required').min(1, 'Response is required')
    }
  }
}

const getInitialValues = (stepData: ResponseStepData): StepData => {
  return {
    parameters: stepData.parameters
  }
}

const getResponseOperationHelpUrl =
  'https://help.workgrid.com/s/article/Setting-Up-an-HTTP-Request-Trigger-Response-for-Workshop'

const ResponseOperation = ({ formik, readOnly = false, trigger }: ResponseProps & FormikValues) => {
  const intl = useIntl()

  const { getVariablesForStep, selectedElement } = useWorkflowFeature()
  const variables = getVariablesForStep()

  // Handle migration of single responses
  useEffect(() => {
    if (selectedElement.data?.config?.stepData['parameters'] === undefined) {
      formik.setFieldValue('stepData.parameters', [
        {
          id: v4(),
          value: selectedElement.data?.config?.stepData['inputVariable'] || ''
        }
      ])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <FieldArray
      name="stepData.parameters"
      validateOnChange={false}
      render={() => (
        <>
          <Label className="mt-4" required>
            {intl.formatMessage(messages.responseData)}
          </Label>
          {formik.values.stepData.parameters?.map((param: any, i: number) => (
            <div className="my-0 flex py-0" key={param.id}>
              <div className="flex w-full flex-col">
                {trigger.type === TriggerTypes.knowledge ? (
                  <div>
                    <p className="py-2 text-xs text-zinc-500">
                      Response body in a Knowledge feature should have the following structure:
                    </p>
                    <Monaco
                      language="json"
                      value={JSON.stringify(
                        [
                          {
                            title: 'Article Title',
                            content: 'Article Contents',
                            url: 'https://help.workgrid.com',
                            id: '1234',
                            score: {
                              type: 'keyword/semantic',
                              score: 1
                            },
                            summary: 'Article Summary' // optional
                          }
                        ],
                        null,
                        2
                      )}
                      className="max-h-64"
                      options={{ readOnly: true, scrollBeyondLastLine: false, lineNumbers: 'off' }}
                    />
                  </div>
                ) : null}
                <VariableInput
                  name={`stepData.parameters[${i}].value`}
                  onChange={value => formik.setFieldValue(`stepData.parameters[${i}].value`, value)}
                  multiline={true}
                  placeholder={
                    trigger.type === TriggerTypes.knowledge ? 'List of articles' : 'Enter text or an adaptive card'
                  }
                  variables={variables}
                  readOnly={readOnly}
                />
              </div>
            </div>
          ))}
        </>
      )}
    />
  )
}

export default connect(ResponseOperation)

export { getInitialValues, getValidationSchema, getResponseOperationHelpUrl }
